import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import QuickLogDialog from './QuickLogDialog';

/** Accuracy of actual vs estimate, as a 0-100 percentage. */
const accuracyPct = (estimated, actual) => {
  if (!estimated) return null;
  const pct = (1 - Math.abs(actual - estimated) / estimated) * 100;
  return Math.max(0, Math.round(pct));
};

const accuracyColor = (pct) => {
  if (pct === null) return 'default';
  if (pct >= 80) return 'success';
  if (pct >= 50) return 'warning';
  return 'error';
};

/**
 * Time logs grouped by task -> action item, showing estimated vs actual per
 * item, with a Quick Log button that POSTs a new entry.
 *
 * @param {object} props
 * @param {Array<object>} props.groups - The /calendar time_logs payload.
 * @param {function} props.onLogged - Called after a new time log is created.
 * @param {dayjs.Dayjs} [props.currentDate] - date a quick log is attributed to.
 */
const TimeLogsPanel = ({ groups = [], onLogged, currentDate }) => {
  const [expanded, setExpanded] = useState({});
  const [quickLogOpen, setQuickLogOpen] = useState(false);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Time logs
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setQuickLogOpen(true)}>
          Quick Log
        </Button>
      </Box>

      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No time logged for this range.
        </Typography>
      )}

      <Stack spacing={1}>
        {groups.map((group) => (
          <Box key={group.task_id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Box
              onClick={() => toggle(group.task_id)}
              sx={{
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {group.task_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.track || ''}
                </Typography>
              </Box>
              <IconButton size="small">
                {expanded[group.task_id] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={expanded[group.task_id] ?? true}>
              <Box sx={{ px: 1, pb: 1 }}>
                {group.action_items.map((item) => {
                  const pct = accuracyPct(item.estimated, item.actual);
                  return (
                    <Box
                      key={item.action_item_id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                        {item.description}
                      </Typography>
                      <Typography variant="caption" sx={{ mr: 1 }} color="text.secondary">
                        est {item.estimated}h · act {item.actual}h
                      </Typography>
                      <Chip
                        size="small"
                        color={accuracyColor(pct)}
                        label={pct === null ? '—' : `${pct}%`}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        ))}
      </Stack>

      <QuickLogDialog
        open={quickLogOpen}
        defaultDate={currentDate}
        onClose={() => setQuickLogOpen(false)}
        onLogged={() => {
          setQuickLogOpen(false);
          onLogged?.();
        }}
      />
    </Paper>
  );
};

export default TimeLogsPanel;
